import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AgentType } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { llmChatApi } from '@/lib/api/llm-chat';

type OpenProjectAgentButtonProps =
  | { projectConceptId: string; inquiryId?: never }
  | { inquiryId: string; projectConceptId?: never };

/** Starts a Project Agent chat linked to the given record. Renders nothing when the agent is not available. */
export function OpenProjectAgentButton(props: OpenProjectAgentButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const { data: configs } = useQuery({
    queryKey: ['agent-configs'],
    queryFn: () => llmChatApi.getAgentConfigs(),
  });

  if (!configs?.some((c) => c.agentType === AgentType.ProjectAgent)) {
    return null;
  }

  const handleClick = async () => {
    setStarting(true);
    try {
      const session = await llmChatApi.createSession({
        agentType: AgentType.ProjectAgent,
        ...(props.projectConceptId
          ? { projectConceptId: props.projectConceptId }
          : { inquiryId: props.inquiryId }),
      });
      navigate(`/llm-chat/${session.id}`);
    } catch {
      toast.error(t('projectAgent.openChatError'));
      setStarting(false);
    }
  };

  return (
    <Button type="button" variant="outline" onClick={() => void handleClick()} disabled={starting}>
      <ClipboardCheck className="h-4 w-4" />
      {t('projectAgent.openChat')}
    </Button>
  );
}
